import { enablePatches, produce, applyPatches } from 'immer';
import { createSelector } from '@ngrx/store';
const defaultConfig = {
  allowedActionTypes: [],
  mergeActionTypes: [],
  mergeRules: {},
  maxBufferSize: 32,
  undoActionType: "UNDO",
  redoActionType: "REDO",
  breakMergeActionType: "BREAK_MERGE",
  clearActionType: "CLEAR",
  segmentationOverride: () => undefined,
  trackActionPayload: false
};
const produceOn = (...args) => {
  const reducer = args.pop();
  const types = args.map(creator => creator.type);
  return {
    reducer,
    types
  };
};
const initialUndoRedoState = {
  histories: {}
};
const DEFAULT_KEY = "DEFAULT";
const undoRedo = (config = {}) => {
  enablePatches();
  return {
    createUndoRedoReducer: (initialState, ...ons) => create(initialState, ons, config),
    createSegmentedUndoRedoReducer: (initialState, segmenter, ...ons) => create(initialState, ons, config, segmenter),
    wrapReducer: (reducer, segmenter) => wrap(reducer, config, segmenter)
  };
};
const create = (initialState, ons, config, segmenter) => {
  const map = {};
  for (const on of ons) {
    for (const type of on.types) {
      if (map[type]) {
        const existingReducer = map[type];
        map[type] = (state, action) => on.reducer(existingReducer(state, action), action);
      } else {
        map[type] = on.reducer;
      }
    }
  }
  const reducer = (state = initialState, action, listener) => {
    const r = map[action.type];
    if (r) {
      return produce(state, draft => r(draft, action), listener);
    }
    return state;
  };
  return wrap(reducer, config, segmenter);
};
const wrap = (reducer, config, segmenter = () => DEFAULT_KEY) => {
  const {
    allowedActionTypes,
    mergeActionTypes,
    mergeRules,
    maxBufferSize,
    undoActionType,
    redoActionType,
    breakMergeActionType,
    clearActionType,
    segmentationOverride,
    trackActionPayload
  } = {
    ...defaultConfig,
    ...config
  };
  const isUndoable = action => !allowedActionTypes.length || allowedActionTypes.some(type => type === action.type);
  const shouldMerge = (a, b) => a.type === b.type && (mergeActionTypes.includes(a.type) || (mergeRules[a.type]?.(a, b) ?? false));
  const slimAction = action => {
    let track;
    if (typeof trackActionPayload === "boolean") {
      track = trackActionPayload;
    } else {
      track = trackActionPayload(action);
    }
    return track ? action : {
      type: action.type
    };
  };
  const segmentationKey = (state, action) => action && segmentationOverride(action) || segmenter(state);
  return (state, action) => {
    if (!state) {
      return reducer(state, action);
    }
    const key = segmentationKey(state, action);
    const history = state.histories[key] || {
      undoable: [],
      undone: [],
      mergeBroken: false
    };
    switch (action.type) {
      case undoActionType:
        {
          const [undoStep, ...remainingUndoable] = history.undoable;
          if (undoStep) {
            const prependedUndone = [undoStep, ...history.undone];
            const undoneState = {
              ...applyPatches(state, undoStep.patches.inversePatches),
              histories: {
                ...state.histories,
                [key]: {
                  ...history,
                  undoable: remainingUndoable,
                  undone: prependedUndone
                }
              }
            };
            return reducer(undoneState, action);
          }
          return state;
        }
      case redoActionType:
        {
          const [redoStep, ...remainingUndone] = history.undone;
          if (redoStep) {
            const prependedUndoable = [redoStep, ...history.undoable];
            const redoneState = {
              ...applyPatches(state, redoStep.patches.patches),
              histories: {
                ...state.histories,
                [key]: {
                  ...history,
                  undoable: prependedUndoable,
                  undone: remainingUndone
                }
              }
            };
            return reducer(redoneState, action);
          }
          return state;
        }
      case clearActionType:
        {
          return {
            ...state,
            histories: {
              ...state.histories,
              [key]: {
                undoable: [],
                undone: [],
                mergeBroken: false
              }
            }
          };
        }
      case breakMergeActionType:
        {
          return {
            ...state,
            histories: {
              ...state.histories,
              [key]: {
                ...history,
                mergeBroken: true
              }
            }
          };
        }
      default:
        {
          let listener;
          let patchedHistory;
          if (isUndoable(action)) {
            listener = (patches, inversePatches) => {
              const [lastStep, ...otherSteps] = history.undoable;
              let undoable;
              if (patches.length) {
                const [lastAction, ...prevActions] = lastStep?.actions || [];
                const slimmedAction = slimAction(action);
                if (lastAction && !history.mergeBroken && shouldMerge(lastAction, action)) {
                  undoable = [{
                    patches: {
                      // merge patches for consecutive actions of same type
                      patches: [...lastStep.patches.patches, ...patches],
                      inversePatches: [...inversePatches, ...lastStep.patches.inversePatches]
                    },
                    actions: [slimmedAction, lastAction, ...prevActions]
                  }, ...otherSteps];
                } else {
                  undoable = [
                  // remember differences while dropping at buffer max-size
                  {
                    patches: {
                      patches,
                      inversePatches
                    },
                    actions: [slimmedAction]
                  }, ...history.undoable.slice(0, maxBufferSize - 1)];
                }
                patchedHistory = {
                  undoable,
                  undone: [],
                  // clear redo stack
                  mergeBroken: false
                };
              }
            };
          }
          const nextState = reducer(state, action, listener);
          if (patchedHistory) {
            return {
              ...nextState,
              histories: {
                ...state.histories,
                [key]: patchedHistory
              }
            };
          }
          return nextState;
        }
    }
  };
};
const createHistorySelectors = (selector, segmenter = () => DEFAULT_KEY) => {
  const selectHistory = props => createSelector(selector, state => state.histories[props?.key ?? segmenter(state)]);
  const selectCanUndo = props => createSelector(selectHistory(props), history => history && history.undoable.length > 0);
  const selectCanRedo = props => createSelector(selectHistory(props), history => history && history.undone.length > 0);
  return {
    selectHistory,
    selectCanUndo,
    selectCanRedo
  };
};

/*
 * Public API Surface of ngrx-wieder
 */

/**
 * Generated bundle index. Do not edit.
 */

export { DEFAULT_KEY, createHistorySelectors, defaultConfig, initialUndoRedoState, produceOn, undoRedo };
